import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Lock,
    LogOut,
    Loader2,
    Plus,
    Trash2,
    Mail,
    Phone,
    MapPin,
    Upload,
    X,
    Image as ImageIcon,
    AlertTriangle,
    BadgeCheck,
    ChevronDown,
    Search,
    Pencil,
    Save,
    Map as MapIcon,
    Download,
    FileUp,
    Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "mfs_admin_token";

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);

    const verify = useCallback(async (t) => {
        try {
            await axios.get(`${API}/admin/verify`, { headers: { "X-Admin-Token": t } });
            setAuthed(true);
        } catch {
            setAuthed(false);
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        if (token) {
            verify(token);
        } else {
            setChecking(false);
        }
    }, [token, verify]);

    const onLogin = async (e) => {
        e.preventDefault();
        if (!token.trim()) {
            toast.error("Enter the admin passcode.");
            return;
        }
        setChecking(true);
        try {
            await axios.get(`${API}/admin/verify`, { headers: { "X-Admin-Token": token } });
            localStorage.setItem(TOKEN_KEY, token);
            setAuthed(true);
            toast.success("Welcome back.");
        } catch {
            toast.error("Invalid passcode.");
            setAuthed(false);
        } finally {
            setChecking(false);
        }
    };

    const onLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
    };

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F1E8D5]">
                <Loader2 className="animate-spin text-[#C2410C]" size={32} />
            </div>
        );
    }

    if (!authed) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F1E8D5] p-6">
                <form
                    onSubmit={onLogin}
                    className="w-full max-w-md bg-white border-2 border-black rounded-3xl p-8 shadow-[8px_8px_0_0_#0A0A0A]"
                    data-testid="admin-login-form"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="w-11 h-11 rounded-xl bg-[#C2410C] border-2 border-black flex items-center justify-center">
                            <Lock size={20} className="text-white" />
                        </span>
                        <h1 className="font-display font-black text-2xl tracking-tighter">
                            Admin access
                        </h1>
                    </div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest font-bold text-stone-600 mb-2">
                        Passcode
                    </label>
                    <input
                        type="password"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="••••••••"
                        autoFocus
                        className="w-full px-4 py-3 bg-[#F1E8D5] border-2 border-black rounded-full text-black font-medium outline-none focus:bg-white"
                        data-testid="admin-passcode-input"
                    />
                    <button
                        type="submit"
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#C2410C] text-white font-medium rounded-full px-6 py-3.5 border-2 border-black transition-all active:scale-95"
                        data-testid="admin-login-btn"
                    >
                        Enter
                    </button>
                </form>
            </div>
        );
    }

    return <AdminDashboard token={token} onLogout={onLogout} />;
}

function AdminDashboard({ token, onLogout }) {
    const auth = useMemo(() => ({ headers: { "X-Admin-Token": token } }), [token]);
    const [tab, setTab] = useState("partners");

    return (
        <div className="min-h-screen bg-[#F1E8D5] text-black">
            <header className="border-b-2 border-black bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-2.5">
                        <span className="font-display font-bold uppercase text-sm tracking-tight">
                            MFS Admin
                        </span>
                    </a>
                    <button
                        onClick={onLogout}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-black text-sm font-medium hover:bg-[#F1E8D5]"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut size={14} />
                        Sign out
                    </button>
                </div>
                <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-2 -mb-px">
                    {[
                        { id: "partners", label: "Partners" },
                        { id: "data", label: "Data" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-5 py-3 border-2 border-b-0 rounded-t-2xl text-sm font-medium transition-colors ${
                                tab === t.id
                                    ? "bg-[#F1E8D5] border-black"
                                    : "bg-transparent border-transparent text-stone-600 hover:text-black"
                            }`}
                            data-testid={`admin-tab-${t.id}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                {tab === "partners" && <TeachersAdmin auth={auth} />}
                {tab === "data" && <DataAdmin auth={auth} />}
            </main>
        </div>
    );
}

/* =========================  TEACHERS  ========================= */

const EMPTY_TEACHER = {
    name: "",
    bio: "",
    genres: "",
    experience_years: "",
    postcode: "",
    phone: "",
    email: "",
    in_person: true,
    online: false,
    rate_per_hour_gbp: "",
    photo_url: "",
    // Partner/Territory
    territory_id: "",
    partner_id: "",
    certified_partner: false,
    partner_status: "Inactive",
    consultation_booking_url: "",
};

const MAX_PHOTO_BYTES = 2 * 1024 * 1024; // 2 MB — base64 lives in Mongo, keep it lean
const MAX_SOURCE_BYTES = 15 * 1024 * 1024; // Accept up to 15 MB before compression
const TARGET_WIDTH = 800; // Coach cards render at ~400px, 2x for retina
const JPEG_QUALITY = 0.85;

/**
 * Resize an image file client-side to TARGET_WIDTH (preserving aspect), re-encode
 * as JPEG at JPEG_QUALITY, and return a data URL. Passes tiny images through untouched.
 */
async function compressImage(file) {
    // Cheap early-out for already-tiny images (< 200 KB) — no visible gain, keep the original.
    if (file.size < 200 * 1024 && file.type === "image/jpeg") {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ""));
            reader.onerror = () => reject(new Error("read"));
            reader.readAsDataURL(file);
        });
    }

    const bitmap = await createImageBitmap(file);
    const ratio = bitmap.height / bitmap.width;
    const targetW = bitmap.width > TARGET_WIDTH ? TARGET_WIDTH : bitmap.width;
    const targetH = Math.round(targetW * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

function TeachersAdmin({ auth }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState(EMPTY_TEACHER);
    const [territories, setTerritories] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const formRef = useRef(null);

    const isEditing = !!editingId;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r, ter, dup] = await Promise.all([
                axios.get(`${API}/teachers`, { ...auth, params: { limit: 1000 } }),
                axios.get(`${API}/territories`, auth),
                axios.get(`${API}/teachers/duplicate-territories`, auth),
            ]);
            setList(r.data);
            setTerritories(ter.data);
            setDuplicates(dup.data);
        } catch {
            toast.error("Could not load admin data.");
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        load();
    }, [load]);

    const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const startEdit = (t) => {
        setEditingId(t.id);
        setForm({
            name: t.name || "",
            bio: t.bio || "",
            genres: (t.genres || []).join(", "),
            experience_years: t.experience_years ?? "",
            postcode: t.postcode || "",
            phone: t.phone || "",
            email: t.email || "",
            in_person: !!t.in_person,
            online: !!t.online,
            rate_per_hour_gbp: t.rate_per_hour_gbp ?? "",
            photo_url: t.photo_url || "",
            territory_id: t.territory_id || "",
            partner_id: t.partner_id || "",
            certified_partner: !!t.certified_partner,
            partner_status: t.partner_status || "Inactive",
            consultation_booking_url: t.consultation_booking_url || "",
        });
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(EMPTY_TEACHER);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.postcode.trim()) {
            toast.error("Name and postcode are required.");
            return;
        }
        if (!form.territory_id) {
            toast.error("Please select a territory.");
            return;
        }
        setSubmitting(true);
        const payload = {
            name: form.name.trim(),
            bio: form.bio.trim(),
            genres: form.genres
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            postcode: form.postcode.trim(),
            phone: form.phone.trim() || null,
            email: form.email.trim() || null,
            in_person: !!form.in_person,
            online: !!form.online,
            experience_years: form.experience_years ? Number(form.experience_years) : null,
            rate_per_hour_gbp: form.rate_per_hour_gbp ? Number(form.rate_per_hour_gbp) : null,
            photo_url: form.photo_url ? form.photo_url.trim() : null,
            territory_id: form.territory_id,
            partner_id: form.partner_id.trim() || null,
            certified_partner: !!form.certified_partner,
            partner_status: form.partner_status || "Inactive",
            consultation_booking_url: form.consultation_booking_url.trim() || null,
        };
        try {
            if (isEditing) {
                await axios.patch(`${API}/teachers/${editingId}`, payload, auth);
                toast.success(`${payload.name} updated.`);
            } else {
                await axios.post(`${API}/teachers`, payload, auth);
                toast.success(`${payload.name} added.`);
            }
            setEditingId(null);
            setForm(EMPTY_TEACHER);
            load();
        } catch (err) {
            const detail =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                (isEditing ? "Could not update partner." : "Could not add partner.");
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (t) => {
        if (!window.confirm(`Remove ${t.name}?`)) return;
        try {
            await axios.delete(`${API}/teachers/${t.id}`, auth);
            toast.success(`${t.name} removed.`);
            setList((s) => s.filter((x) => x.id !== t.id));
            if (editingId === t.id) cancelEdit();
            const dup = await axios.get(`${API}/teachers/duplicate-territories`, auth);
            setDuplicates(dup.data);
        } catch {
            toast.error("Could not remove partner.");
        }
    };

    return (
        <div className="space-y-8">
            <TerritoryCoverageMap
                auth={auth}
                refreshKey={list.length}
                onSelectTerritory={(territoryId) => {
                    if (isEditing) cancelEdit();
                    setField("territory_id", territoryId);
                    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    toast.success("Territory pre-selected. Fill in the Partner details.");
                }}
            />

            {duplicates.length > 0 && (
                <div
                    className="bg-red-50 border-2 border-red-700 rounded-2xl p-5 flex items-start gap-3"
                    data-testid="admin-duplicate-warning"
                >
                    <AlertTriangle size={22} className="text-red-700 shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-display font-bold text-lg text-red-800">
                            Duplicate territory assignments
                        </h3>
                        <p className="text-sm text-red-800/90 mt-1">
                            The public lookup returns the first Active+Certified partner it
                            finds, so please resolve these to keep it deterministic:
                        </p>
                        <ul className="mt-3 space-y-1 text-sm text-red-900">
                            {duplicates.map((d) => (
                                <li key={d.territory_id}>
                                    <span className="font-mono font-bold">{d.territory_id}</span>{" "}
                                    <span className="font-medium">{d.territory_name}</span>
                                    <span className="text-red-800/70">
                                        {" "}— {d.partners.map((p) => p.name).join(", ")}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-8">
            {/* Add / Edit form */}
            <section className="lg:col-span-5" ref={formRef}>
                <div className="flex items-center justify-between gap-3">
                    <h2 className="font-display font-black text-3xl tracking-tighter" data-testid="admin-form-heading">
                        {isEditing ? "Edit Partner" : "Add a Partner"}
                    </h2>
                    {isEditing && (
                        <button
                            type="button"
                            onClick={cancelEdit}
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border-2 border-black bg-white hover:bg-[#F1E8D5]"
                            data-testid="admin-cancel-edit"
                        >
                            <X size={12} />
                            Cancel
                        </button>
                    )}
                </div>
                <p className="mt-2 text-stone-600 text-sm">
                    {isEditing
                        ? "Update this Partner's profile. Changes go live immediately."
                        : "Assign the Partner to one of the 250 My First Single territories. The territory (not the postcode) defines their public catchment area."}
                </p>
                <form
                    onSubmit={onSubmit}
                    className="mt-6 bg-white border-2 border-black rounded-2xl p-6 shadow-[5px_5px_0_0_#0A0A0A] space-y-4"
                    data-testid="admin-teacher-form"
                >
                    <Field label="Name *">
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setField("name", e.target.value)}
                            placeholder="Sophie Bennett"
                            className={inputCls}
                            data-testid="admin-teacher-name"
                        />
                    </Field>
                    <Field label="Territory *">
                        <TerritorySelect
                            territories={territories}
                            value={form.territory_id}
                            onChange={(v) => setField("territory_id", v)}
                        />
                    </Field>
                    <PhotoUpload
                        value={form.photo_url}
                        onChange={(v) => setField("photo_url", v)}
                    />
                    <Field label="Business postcode *">
                        <input
                            type="text"
                            value={form.postcode}
                            onChange={(e) => setField("postcode", e.target.value.toUpperCase())}
                            placeholder="SW1A 1AA"
                            className={inputCls}
                            data-testid="admin-teacher-postcode"
                        />
                    </Field>
                    <Field label="Bio">
                        <textarea
                            rows={3}
                            value={form.bio}
                            onChange={(e) => setField("bio", e.target.value)}
                            placeholder="Short bio shown to learners…"
                            className={inputCls + " resize-none"}
                        />
                    </Field>
                    <Field label="Genres (comma separated)">
                        <input
                            type="text"
                            value={form.genres}
                            onChange={(e) => setField("genres", e.target.value)}
                            placeholder="Pop, Musical Theatre, R&B"
                            className={inputCls}
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Years experience">
                            <input
                                type="number"
                                value={form.experience_years}
                                onChange={(e) => setField("experience_years", e.target.value)}
                                placeholder="8"
                                className={inputCls}
                                min={0}
                                max={80}
                            />
                        </Field>
                        <Field label="Rate £/hr">
                            <input
                                type="number"
                                value={form.rate_per_hour_gbp}
                                onChange={(e) => setField("rate_per_hour_gbp", e.target.value)}
                                placeholder="50"
                                className={inputCls}
                                min={0}
                                max={500}
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Phone">
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => setField("phone", e.target.value)}
                                placeholder="+44 …"
                                className={inputCls}
                            />
                        </Field>
                        <Field label="Email">
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setField("email", e.target.value)}
                                placeholder="coach@…"
                                className={inputCls}
                            />
                        </Field>
                    </div>
                    <div className="flex gap-5 pt-1">
                        <Checkbox
                            checked={form.in_person}
                            onChange={(v) => setField("in_person", v)}
                            label="In‑person"
                        />
                        <Checkbox
                            checked={form.online}
                            onChange={(v) => setField("online", v)}
                            label="Online"
                        />
                    </div>

                    <div className="pt-4 border-t border-black/10 space-y-4">
                        <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-stone-600">
                            Partner details
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Partner ID">
                                <input
                                    type="text"
                                    value={form.partner_id}
                                    onChange={(e) => setField("partner_id", e.target.value)}
                                    placeholder="Optional"
                                    className={inputCls}
                                    data-testid="admin-partner-id"
                                />
                            </Field>
                            <Field label="Partner status">
                                <select
                                    value={form.partner_status}
                                    onChange={(e) => setField("partner_status", e.target.value)}
                                    className={inputCls}
                                    data-testid="admin-partner-status"
                                >
                                    <option value="Inactive">Inactive</option>
                                    <option value="Active">Active</option>
                                </select>
                            </Field>
                        </div>
                        <Checkbox
                            checked={form.certified_partner}
                            onChange={(v) => setField("certified_partner", v)}
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    <BadgeCheck size={13} className="text-[#C2410C]" />
                                    Certified My First Single Partner
                                </span>
                            }
                            testId="admin-partner-certified"
                        />
                        <Field label="Consultation booking URL">
                            <input
                                type="url"
                                value={form.consultation_booking_url}
                                onChange={(e) => setField("consultation_booking_url", e.target.value)}
                                placeholder="https://calendly.com/…"
                                className={inputCls}
                                data-testid="admin-booking-url"
                            />
                        </Field>
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#1B4D3E] disabled:opacity-60 text-white font-medium rounded-full px-6 py-3 border-2 border-black transition-all active:scale-95"
                        data-testid="admin-teacher-submit"
                    >
                        {submitting ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : isEditing ? (
                            <Save size={16} />
                        ) : (
                            <Plus size={16} />
                        )}
                        {submitting
                            ? "Saving\u2026"
                            : isEditing
                            ? "Save changes"
                            : "Add Partner"}
                    </button>
                </form>
            </section>

            {/* Roster */}
            <section className="lg:col-span-7">
                <div className="flex items-baseline justify-between">
                    <h2 className="font-display font-black text-3xl tracking-tighter">Roster</h2>
                    <span className="font-mono text-xs uppercase tracking-widest font-bold text-stone-500">
                        {list.length} vocal coaches
                    </span>
                </div>
                {loading ? (
                    <div className="mt-6 flex items-center gap-2 text-stone-600">
                        <Loader2 size={16} className="animate-spin" />
                        Loading…
                    </div>
                ) : list.length === 0 ? (
                    <p className="mt-6 text-stone-600">No vocal coaches yet — add your first using the form.</p>
                ) : (
                    <ul className="mt-6 space-y-3">
                        {list.map((t) => (
                            <li
                                key={t.id}
                                className={`bg-white border-2 rounded-2xl p-5 flex items-start gap-4 transition-colors ${
                                    editingId === t.id
                                        ? "border-[#C2410C] shadow-[4px_4px_0_0_#C2410C]"
                                        : "border-black"
                                }`}
                                data-testid={`admin-teacher-row-${t.id}`}
                            >
                                <div className="shrink-0 w-14 h-14 rounded-xl border-2 border-black overflow-hidden bg-[#F1E8D5]">
                                    {t.photo_url ? (
                                        <img
                                            src={t.photo_url}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-stone-400 font-display font-black text-lg">
                                            {t.name?.[0] || "?"}
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-display font-bold text-lg tracking-tight">{t.name}</h3>
                                        {t.certified_partner && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-black bg-[#D4A017] text-black"
                                                title="Certified My First Single Partner"
                                            >
                                                <BadgeCheck size={10} />
                                                Certified
                                            </span>
                                        )}
                                        <span
                                            className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-black ${
                                                t.partner_status === "Active"
                                                    ? "bg-[#1B4D3E] text-white"
                                                    : "bg-stone-200 text-stone-700"
                                            }`}
                                        >
                                            <span className={`w-1.5 h-1.5 rounded-full ${t.partner_status === "Active" ? "bg-[#D4A017]" : "bg-stone-500"}`} />
                                            {t.partner_status || "Inactive"}
                                        </span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-stone-600">
                                        {t.territory_name && (
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin size={11} />
                                                {t.territory_name}
                                                <span className="font-mono text-stone-400">
                                                    · {t.territory_id}
                                                </span>
                                            </span>
                                        )}
                                        <span className="text-stone-400">·</span>
                                        <span>Base: {t.postcode}</span>
                                    </div>
                                    {t.genres?.length > 0 && (
                                        <div className="mt-1.5 flex flex-wrap gap-1">
                                            {t.genres.map((g) => (
                                                <span
                                                    key={g}
                                                    className="px-2 py-0.5 rounded-full border border-black text-[10px] bg-[#F1E8D5]"
                                                >
                                                    {g}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-700">
                                        {t.experience_years != null && (
                                            <span>{t.experience_years} yrs exp</span>
                                        )}
                                        {t.rate_per_hour_gbp != null && <span>£{t.rate_per_hour_gbp}/hr</span>}
                                        {t.phone && (
                                            <span className="inline-flex items-center gap-1">
                                                <Phone size={11} /> {t.phone}
                                            </span>
                                        )}
                                        {t.email && (
                                            <span className="inline-flex items-center gap-1">
                                                <Mail size={11} /> {t.email}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="shrink-0 flex flex-col gap-1.5">
                                    <button
                                        onClick={() => startEdit(t)}
                                        className={`p-2 rounded-full border-2 border-black transition-colors ${
                                            editingId === t.id
                                                ? "bg-[#C2410C] text-white"
                                                : "hover:bg-[#F1E8D5]"
                                        }`}
                                        title="Edit"
                                        data-testid={`admin-teacher-edit-${t.id}`}
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        onClick={() => onDelete(t)}
                                        className="p-2 rounded-full border-2 border-black hover:bg-red-50 hover:border-red-700 hover:text-red-700 transition-colors"
                                        title="Remove"
                                        data-testid={`admin-teacher-delete-${t.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
            </div>
        </div>
    );
}

/* =========================  DATA  ========================= */

function DataAdmin({ auth }) {
    return (
        <div className="space-y-8">
            <PostcodeLookup auth={auth} />
            <TerritoriesTable auth={auth} />
            <DataImportExport auth={auth} />
        </div>
    );
}

function PostcodeLookup({ auth }) {
    const [postcode, setPostcode] = useState("");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const run = async (e) => {
        e.preventDefault();
        if (!postcode.trim()) return;
        setBusy(true);
        try {
            const r = await axios.get(`${API}/lookup`, {
                ...auth,
                params: { postcode: postcode.trim() },
            });
            setResult(r.data);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Lookup failed"
            );
            setResult(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            className="bg-white border-2 border-black rounded-3xl p-6 md:p-8 shadow-[6px_6px_0_0_#0A0A0A]"
            data-testid="admin-postcode-lookup"
        >
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter inline-flex items-center gap-2.5">
                <Search size={20} />
                Postcode diagnostic
            </h2>
            <p className="mt-2 text-stone-600 text-sm">
                Trace any UK postcode through the district → territory → Partner
                pipeline. Useful for confirming edge cases.
            </p>
            <form onSubmit={run} className="mt-4 flex gap-3">
                <input
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                    placeholder="e.g. SW1A 1AA"
                    className={inputCls + " flex-1"}
                    data-testid="admin-lookup-input"
                />
                <button
                    type="submit"
                    disabled={busy}
                    className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 border-2 border-black text-sm font-medium hover:bg-[#1B4D3E] disabled:opacity-60"
                    data-testid="admin-lookup-btn"
                >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                    Trace
                </button>
            </form>

            {result && (
                <div
                    className="mt-5 grid md:grid-cols-3 gap-3 md:gap-4"
                    data-testid="admin-lookup-result"
                >
                    <ResultTile
                        label="District"
                        primary={result.postcode_district || "\u2014"}
                        secondary={`from ${result.postcode}`}
                        ok={!!result.postcode_district}
                    />
                    <ResultTile
                        label="Territory"
                        primary={result.territory?.territory_name || "No coverage"}
                        secondary={result.territory?.territory_id}
                        ok={!!result.territory}
                    />
                    <ResultTile
                        label="Partner"
                        primary={result.partner?.name || "None assigned"}
                        secondary={
                            result.partner
                                ? `${result.partner.partner_status}${result.partner.certified_partner ? " · Certified" : ""}`
                                : undefined
                        }
                        ok={!!result.partner}
                    />
                </div>
            )}
        </section>
    );
}

function ResultTile({ label, primary, secondary, ok }) {
    return (
        <div className="border-2 border-black rounded-2xl p-4 bg-[#F1E8D5]">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-stone-600">
                    {label}
                </div>
                {ok ? (
                    <Check size={13} className="text-[#1B4D3E]" />
                ) : (
                    <X size={13} className="text-[#C2410C]" />
                )}
            </div>
            <div className="mt-1 font-display font-black text-xl tracking-tight text-black leading-tight">
                {primary}
            </div>
            {secondary && (
                <div className="mt-0.5 text-xs font-mono text-stone-500">{secondary}</div>
            )}
        </div>
    );
}

function TerritoriesTable({ auth }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState("all");
    const [partnerMap, setPartnerMap] = useState({}); // territory_id -> partner name

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ter, pars] = await Promise.all([
                axios.get(`${API}/territories`, auth),
                axios.get(`${API}/teachers`, { ...auth, params: { limit: 2000 } }),
            ]);
            setRows(ter.data);
            const map = {};
            pars.data.forEach((p) => {
                if (p.partner_status === "Active" && p.certified_partner) {
                    map[p.territory_id] = p.name;
                }
            });
            setPartnerMap(map);
        } catch {
            toast.error("Could not load territories.");
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        load();
    }, [load]);

    const q = query.trim().toLowerCase();
    const filtered = rows
        .filter(
            (t) =>
                (status === "all" ||
                    (status === "covered" && partnerMap[t.territory_id]) ||
                    (status === "uncovered" && !partnerMap[t.territory_id])) &&
                (!q ||
                    t.territory_id.toLowerCase().includes(q) ||
                    (t.territory_name || "").toLowerCase().includes(q) ||
                    (t.region || "").toLowerCase().includes(q) ||
                    (t.anchor_town || "").toLowerCase().includes(q))
        )
        .sort((a, b) => a.territory_id.localeCompare(b.territory_id));

    const startEdit = (t) => {
        setEditingId(t.territory_id);
        setEditValues({
            territory_name: t.territory_name || "",
            region: t.region || "",
            nation: t.nation || "",
            anchor_town: t.anchor_town || "",
            territory_type: t.territory_type || "",
            territory_status: t.territory_status || "Available",
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditValues({});
    };

    const saveEdit = async () => {
        if (!editingId) return;
        setSaving(true);
        try {
            await axios.patch(`${API}/territories/${editingId}`, editValues, auth);
            toast.success(`${editingId} updated.`);
            setEditingId(null);
            setEditValues({});
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="bg-white border-2 border-black rounded-3xl p-6 md:p-8 shadow-[6px_6px_0_0_#0A0A0A]"
            data-testid="admin-territories-table"
        >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter inline-flex items-center gap-2.5">
                    <MapPin size={20} />
                    Territories
                    <span className="text-sm text-stone-500 font-mono font-medium ml-2">
                        {filtered.length} / {rows.length}
                    </span>
                </h2>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500"
                    />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by ID, name, region or town\u2026"
                        className={inputCls + " pl-9"}
                        data-testid="admin-territories-search"
                    />
                </div>
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={inputCls + " w-auto"}
                    data-testid="admin-territories-filter"
                >
                    <option value="all">All statuses</option>
                    <option value="covered">Covered only</option>
                    <option value="uncovered">Uncovered only</option>
                </select>
            </div>

            <div className="mt-5 overflow-x-auto rounded-2xl border-2 border-black">
                <table className="w-full text-sm">
                    <thead className="bg-[#F1E8D5] border-b-2 border-black text-left">
                        <tr>
                            <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">ID</th>
                            <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                            <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Region</th>
                            <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Anchor Town</th>
                            <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Status</th>
                            <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Partner</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="px-3 py-6 text-center text-stone-500">
                                    <Loader2 size={16} className="animate-spin inline" />
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-3 py-6 text-center text-stone-500">
                                    No territories match.
                                </td>
                            </tr>
                        ) : (
                            filtered.map((t) => {
                                const isEditing = editingId === t.territory_id;
                                const partner = partnerMap[t.territory_id];
                                if (isEditing) {
                                    return (
                                        <tr
                                            key={t.territory_id}
                                            className="bg-[#F1E8D5] border-t-2 border-[#C2410C]"
                                            data-testid={`admin-territory-row-${t.territory_id}`}
                                        >
                                            <td className="px-3 py-2 font-mono text-[11px] align-top pt-3">{t.territory_id}</td>
                                            <td className="px-3 py-2 align-top">
                                                <input
                                                    className={inputCls + " py-1.5 text-sm"}
                                                    value={editValues.territory_name}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, territory_name: e.target.value }))}
                                                    data-testid={`admin-territory-name-input-${t.territory_id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <input
                                                    className={inputCls + " py-1.5 text-sm"}
                                                    value={editValues.region}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, region: e.target.value }))}
                                                />
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <input
                                                    className={inputCls + " py-1.5 text-sm"}
                                                    value={editValues.anchor_town}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, anchor_town: e.target.value }))}
                                                />
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <input
                                                    className={inputCls + " py-1.5 text-sm"}
                                                    value={editValues.territory_status}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, territory_status: e.target.value }))}
                                                />
                                            </td>
                                            <td className="px-3 py-2 align-top text-stone-600 text-xs">{partner || "—"}</td>
                                            <td className="px-3 py-2 align-top">
                                                <div className="flex gap-1.5">
                                                    <button
                                                        onClick={saveEdit}
                                                        disabled={saving}
                                                        className="p-1.5 rounded-full border-2 border-black bg-[#1B4D3E] text-white hover:bg-black disabled:opacity-60"
                                                        title="Save"
                                                        data-testid={`admin-territory-save-${t.territory_id}`}
                                                    >
                                                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="p-1.5 rounded-full border-2 border-black hover:bg-white"
                                                        title="Cancel"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                                return (
                                    <tr
                                        key={t.territory_id}
                                        className="border-t border-black/10 hover:bg-[#F1E8D5]/60"
                                        data-testid={`admin-territory-row-${t.territory_id}`}
                                    >
                                        <td className="px-3 py-2 font-mono text-[11px] text-stone-600">{t.territory_id}</td>
                                        <td className="px-3 py-2 font-medium">{t.territory_name}</td>
                                        <td className="px-3 py-2 text-stone-700">{t.region || "—"}</td>
                                        <td className="px-3 py-2 text-stone-700">{t.anchor_town || "—"}</td>
                                        <td className="px-3 py-2">
                                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border border-black bg-white">
                                                {t.territory_status || "—"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            {partner ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium">
                                                    <BadgeCheck size={12} className="text-[#1B4D3E]" />
                                                    {partner}
                                                </span>
                                            ) : (
                                                <span className="text-stone-400 text-xs italic">Available</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() => startEdit(t)}
                                                className="p-1.5 rounded-full border-2 border-black hover:bg-[#F1E8D5]"
                                                title="Edit"
                                                data-testid={`admin-territory-edit-${t.territory_id}`}
                                            >
                                                <Pencil size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function DataImportExport({ auth }) {
    return (
        <section
            className="bg-white border-2 border-black rounded-3xl p-6 md:p-8 shadow-[6px_6px_0_0_#0A0A0A]"
            data-testid="admin-import-export"
        >
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter inline-flex items-center gap-2.5">
                <FileUp size={20} />
                Import &amp; Export
            </h2>
            <p className="mt-2 text-stone-600 text-sm">
                Download the current data as CSV, or upload an updated CSV to refresh
                Territories or Postcode Mappings. Uploads upsert by primary key
                (existing rows updated, new rows inserted).
            </p>

            <div className="mt-5 grid md:grid-cols-3 gap-4">
                <ExportButton
                    label="Partners CSV"
                    href={`${API}/export/partners.csv`}
                    token={auth.headers["X-Admin-Token"]}
                    filename="partners.csv"
                    testId="admin-export-partners"
                />
                <ExportButton
                    label="Territories CSV"
                    href={`${API}/export/territories.csv`}
                    token={auth.headers["X-Admin-Token"]}
                    filename="territories.csv"
                    testId="admin-export-territories"
                />
                <ExportButton
                    label="Postcode Mappings CSV"
                    href={`${API}/export/postcode-mappings.csv`}
                    token={auth.headers["X-Admin-Token"]}
                    filename="postcode-mappings.csv"
                    testId="admin-export-mappings"
                />
            </div>

            <div className="mt-8 grid md:grid-cols-2 gap-4">
                <ImportBlock
                    label="Import Territories"
                    endpoint={`${API}/import/territories`}
                    auth={auth}
                    hint="Column headers must include territory_id (or 'Territory ID')."
                    testId="admin-import-territories"
                />
                <ImportBlock
                    label="Import Postcode Mappings"
                    endpoint={`${API}/import/postcode-mappings`}
                    auth={auth}
                    hint="Must include postcode_district + territory_id columns."
                    testId="admin-import-mappings"
                />
            </div>
        </section>
    );
}

function ExportButton({ label, href, token, filename, testId }) {
    const [busy, setBusy] = useState(false);
    const onClick = async () => {
        setBusy(true);
        try {
            const r = await axios.get(href, {
                responseType: "blob",
                headers: { "X-Admin-Token": token },
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`${filename} downloaded.`);
        } catch {
            toast.error(`Could not export ${filename}.`);
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={onClick}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#1B4D3E] disabled:opacity-60 text-white rounded-full px-5 py-3 border-2 border-black text-sm font-medium transition-colors"
            data-testid={testId}
        >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {label}
        </button>
    );
}

function ImportBlock({ label, endpoint, auth, hint, testId }) {
    const [file, setFile] = useState(null);
    const [replace, setReplace] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const onSubmit = async () => {
        if (!file) return;
        if (
            replace &&
            !window.confirm(
                "Replace mode wipes ALL existing rows and inserts these. Are you sure?"
            )
        ) {
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await axios.post(endpoint, fd, {
                ...auth,
                params: { replace },
                headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
            });
            setResult(r.data);
            toast.success(
                `Imported ${r.data.total_rows} rows (${r.data.inserted} new, ${r.data.updated} updated)`
            );
            setFile(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="border-2 border-black rounded-2xl p-5 bg-[#F1E8D5]"
            data-testid={testId}
        >
            <div className="font-display font-bold text-lg">{label}</div>
            <p className="text-xs text-stone-600 mt-1">{hint}</p>

            <label className="mt-4 inline-flex items-center gap-2 cursor-pointer bg-white hover:bg-[#F1E8D5] rounded-full px-4 py-2 border-2 border-black text-sm font-medium">
                <FileUp size={13} />
                {file ? file.name : "Choose CSV\u2026"}
                <input
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid={`${testId}-file`}
                />
            </label>

            <label className="mt-3 flex items-center gap-2 text-xs text-stone-700 cursor-pointer">
                <input
                    type="checkbox"
                    checked={replace}
                    onChange={(e) => setReplace(e.target.checked)}
                    className="accent-[#C2410C]"
                    data-testid={`${testId}-replace`}
                />
                Replace all existing rows (destructive)
            </label>

            <button
                onClick={onSubmit}
                disabled={!file || busy}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#1B4D3E] disabled:opacity-60 text-white rounded-full px-5 py-2.5 border-2 border-black text-sm font-medium"
                data-testid={`${testId}-submit`}
            >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Import
            </button>

            {result && (
                <div className="mt-3 text-xs font-mono text-stone-700">
                    {result.total_rows} rows · {result.inserted} inserted · {result.updated} updated
                </div>
            )}
        </div>
    );
}


/* =========================  Helpers  ========================= */

// UK bounding box for lat/lon → SVG projection.
// Slightly generous to leave breathing room around the coastline.
const UK_BOUNDS = { latMin: 49.5, latMax: 61.0, lonMin: -8.7, lonMax: 2.1 };

function TerritoryCoverageMap({ auth, refreshKey, onSelectTerritory }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hover, setHover] = useState(null); // { territory, x, y } — SVG coords for tooltip
    const svgRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API}/territory-coverage`, auth);
                if (!cancelled) setData(r.data);
            } catch {
                if (!cancelled) toast.error("Could not load coverage map.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [auth, refreshKey]);

    const width = 600;
    const height = 720;
    const project = ([lat, lon]) => {
        const x = ((lon - UK_BOUNDS.lonMin) / (UK_BOUNDS.lonMax - UK_BOUNDS.lonMin)) * width;
        const y = ((UK_BOUNDS.latMax - lat) / (UK_BOUNDS.latMax - UK_BOUNDS.latMin)) * height;
        return [x, y];
    };

    const territories = data?.territories || [];
    const coveredCount = data?.covered_territory_count ?? 0;
    const total = data?.total_territories ?? 250;
    const uncoveredCount = data?.uncovered_territory_count ?? total;
    const pct = total > 0 ? Math.round((coveredCount / total) * 100) : 0;

    // Sort so uncovered draws first (underneath) and covered on top for visibility.
    const ordered = [...territories].sort((a, b) => Number(a.covered) - Number(b.covered));

    // Compute tooltip screen position from SVG viewBox coords.
    const tooltipStyle = (() => {
        if (!hover || !svgRef.current) return { display: "none" };
        const rect = svgRef.current.getBoundingClientRect();
        const scaleX = rect.width / width;
        const scaleY = rect.height / height;
        return {
            left: `${hover.x * scaleX + 16}px`,
            top: `${hover.y * scaleY + 16}px`,
        };
    })();

    return (
        <section
            className="bg-white border-2 border-black rounded-3xl p-6 md:p-8 shadow-[6px_6px_0_0_#0A0A0A]"
            data-testid="admin-coverage-map"
        >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter inline-flex items-center gap-2.5">
                    <MapIcon size={22} />
                    Territory coverage
                </h2>
                <div className="flex items-center gap-4 text-xs text-stone-700 font-mono uppercase tracking-widest font-bold">
                    <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#1B4D3E] border border-black" />
                        Active
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#C2410C] border border-black" />
                        Uncovered
                    </span>
                </div>
            </div>

            <div className="mt-4 grid md:grid-cols-3 gap-3 md:gap-4">
                <Stat
                    label="Covered"
                    value={coveredCount}
                    suffix={`/ ${total}`}
                    accent="#1B4D3E"
                    testId="coverage-covered"
                />
                <Stat
                    label="Uncovered"
                    value={uncoveredCount}
                    accent="#C2410C"
                    testId="coverage-uncovered"
                />
                <Stat
                    label="Network coverage"
                    value={`${pct}%`}
                    accent="#D4A017"
                    testId="coverage-percent"
                />
            </div>

            <div className="mt-6 relative rounded-2xl border-2 border-black overflow-hidden bg-[#F1E8D5]">
                {loading ? (
                    <div className="aspect-[5/6] flex items-center justify-center text-stone-600">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : (
                    <>
                        <svg
                            ref={svgRef}
                            viewBox={`0 0 ${width} ${height}`}
                            className="w-full h-auto block"
                            role="img"
                            aria-label="UK territory coverage scatter map"
                            data-testid="admin-coverage-svg"
                            onMouseLeave={() => setHover(null)}
                        >
                            <defs>
                                <radialGradient id="coverageGlow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#D4A017" stopOpacity="0.14" />
                                    <stop offset="100%" stopColor="#D4A017" stopOpacity="0" />
                                </radialGradient>
                            </defs>
                            <rect width={width} height={height} fill="url(#coverageGlow)" />

                            {ordered.map((t) => (
                                <TerritoryGroup
                                    key={t.territory_id}
                                    territory={t}
                                    project={project}
                                    isHover={hover?.territory?.territory_id === t.territory_id}
                                    onHover={(x, y) => setHover({ territory: t, x, y })}
                                    onLeave={() => setHover(null)}
                                    onClick={() => {
                                        if (!t.covered) onSelectTerritory?.(t.territory_id);
                                    }}
                                />
                            ))}
                        </svg>

                        {/* Tooltip */}
                        {hover && (
                            <div
                                className="pointer-events-none absolute z-20 bg-white border-2 border-black rounded-xl shadow-[3px_3px_0_0_#0A0A0A] px-3 py-2 max-w-[240px]"
                                style={tooltipStyle}
                                data-testid="admin-coverage-tooltip"
                            >
                                <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-stone-500">
                                    {hover.territory.territory_id}
                                    {hover.territory.region && (
                                        <span className="text-stone-400"> · {hover.territory.region}</span>
                                    )}
                                </div>
                                <div className="font-display font-bold text-sm mt-0.5 leading-tight">
                                    {hover.territory.territory_name}
                                </div>
                                <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                                    <span
                                        className={`w-2 h-2 rounded-full border border-black ${
                                            hover.territory.covered ? "bg-[#1B4D3E]" : "bg-[#C2410C]"
                                        }`}
                                    />
                                    {hover.territory.covered ? (
                                        <span className="text-stone-700">
                                            <strong className="text-black">{hover.territory.partner_name}</strong>
                                        </span>
                                    ) : (
                                        <span className="text-stone-700">
                                            <em className="not-italic font-medium">Available</em>
                                            <span className="text-stone-500"> — click to onboard</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <p className="mt-4 text-xs text-stone-600 leading-relaxed">
                Each dot is one UK postcode district. A district shows{" "}
                <strong className="text-[#1B4D3E]">green</strong> when its territory
                has an Active + Certified Partner assigned, and{" "}
                <strong className="text-[#C2410C]">terracotta</strong> when the
                territory is still available. Hover any dot to see the territory
                &mdash; click an uncovered one to start onboarding.
            </p>
        </section>
    );
}

function TerritoryGroup({ territory, project, isHover, onHover, onLeave, onClick }) {
    const covered = territory.covered;
    const fill = covered ? "#1B4D3E" : "#C2410C";
    const r = covered ? (isHover ? 3.4 : 2.4) : isHover ? 2.8 : 1.6;
    const opacity = covered ? 0.95 : isHover ? 0.9 : 0.55;

    // Compute a group representative point (average of its districts) for tooltip anchoring.
    const anchor = (() => {
        if (!territory.districts?.length) return null;
        const total = territory.districts.reduce(
            (acc, [lat, lon]) => [acc[0] + lat, acc[1] + lon],
            [0, 0],
        );
        const n = territory.districts.length;
        return project([total[0] / n, total[1] / n]);
    })();

    const handleEnter = () => {
        const pt = anchor || [0, 0];
        onHover(pt[0], pt[1]);
    };

    return (
        <g
            onMouseEnter={handleEnter}
            onMouseLeave={onLeave}
            onClick={onClick}
            style={{ cursor: covered ? "default" : "pointer" }}
            data-testid={`coverage-territory-${territory.territory_id}`}
        >
            {territory.districts.map(([lat, lon], i) => {
                const [x, y] = project([lat, lon]);
                return (
                    <g key={i}>
                        <circle cx={x} cy={y} r={r} fill={fill} opacity={opacity} />
                        {/* Larger transparent hit area for forgiving hover */}
                        <circle cx={x} cy={y} r={7} fill="transparent" />
                    </g>
                );
            })}
        </g>
    );
}

function Stat({ label, value, suffix, accent, testId }) {
    return (
        <div
            className="border-2 border-black rounded-2xl p-4 bg-[#F1E8D5] relative overflow-hidden"
            data-testid={testId}
        >
            <div
                className="absolute -top-2 -right-2 w-16 h-16 rounded-full opacity-15 blur-lg"
                style={{ background: accent }}
                aria-hidden
            />
            <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-stone-600">
                {label}
            </div>
            <div className="mt-1 font-display font-black text-3xl tracking-tighter text-black flex items-baseline gap-2">
                <span>{value}</span>
                {suffix && (
                    <span className="text-sm font-mono text-stone-500 font-normal">
                        {suffix}
                    </span>
                )}
            </div>
        </div>
    );
}

const inputCls =
    "w-full px-4 py-2.5 bg-[#F1E8D5] border-2 border-black rounded-xl text-black outline-none focus:bg-white placeholder:text-stone-500";

function PhotoUpload({ value, onChange }) {
    const isDataUrl = value?.startsWith("data:");
    const [busy, setBusy] = useState(false);

    const onFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file.");
            return;
        }
        if (file.size > MAX_SOURCE_BYTES) {
            toast.error("Photo too large. Please pick one under 15 MB.");
            return;
        }
        setBusy(true);
        try {
            const dataUrl = await compressImage(file);
            // Sanity check: base64 output shouldn't exceed the DB-side cap.
            if (dataUrl.length > MAX_PHOTO_BYTES) {
                toast.error("Compressed photo is still too large. Please try a smaller image.");
                return;
            }
            onChange(dataUrl);
        } catch {
            toast.error("Could not read that image.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <span className="block font-mono text-[10px] uppercase tracking-widest font-bold text-stone-600 mb-1.5">
                Portrait
            </span>
            <div className="flex items-start gap-4">
                <div
                    className="shrink-0 w-24 h-28 rounded-xl border-2 border-black overflow-hidden bg-[#F1E8D5] flex items-center justify-center relative"
                    data-testid="admin-photo-preview"
                >
                    {busy ? (
                        <Loader2 size={22} className="animate-spin text-[#C2410C]" />
                    ) : value ? (
                        <>
                            <img
                                src={value}
                                alt="Coach portrait preview"
                                className="w-full h-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white border-2 border-black flex items-center justify-center hover:bg-red-50 hover:text-red-700"
                                title="Remove photo"
                                data-testid="admin-photo-clear"
                            >
                                <X size={12} />
                            </button>
                        </>
                    ) : (
                        <ImageIcon size={22} className="text-stone-400" />
                    )}
                </div>

                <div className="flex-1 min-w-0 space-y-2">
                    <label
                        className={`inline-flex items-center gap-2 cursor-pointer bg-[#0A0A0A] hover:bg-[#C2410C] text-white text-sm font-medium rounded-full px-4 py-2 border-2 border-black transition-colors ${
                            busy ? "opacity-60 pointer-events-none" : ""
                        }`}
                        data-testid="admin-photo-upload-label"
                    >
                        {busy ? (
                            <>
                                <Loader2 size={13} className="animate-spin" />
                                Compressing…
                            </>
                        ) : (
                            <>
                                <Upload size={13} />
                                {isDataUrl ? "Replace file" : "Upload photo"}
                            </>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(e) => onFile(e.target.files?.[0])}
                            data-testid="admin-photo-upload-input"
                        />
                    </label>
                    <input
                        type="url"
                        value={isDataUrl ? "" : value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="…or paste an image URL"
                        className={inputCls}
                        data-testid="admin-photo-url-input"
                    />
                    <p className="text-[11px] text-stone-500 leading-snug">
                        Any size — we&rsquo;ll shrink it to 800px wide and optimise it for you.
                    </p>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-widest font-bold text-stone-600 mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}

function Checkbox({ checked, onChange, label, testId }) {
    return (
        <label className="inline-flex items-center gap-2 cursor-pointer text-sm" data-testid={testId}>
            <span
                className={`w-5 h-5 rounded border-2 border-black inline-flex items-center justify-center ${
                    checked ? "bg-[#0A0A0A] text-white" : "bg-white"
                }`}
                onClick={() => onChange(!checked)}
            >
                {checked && "✓"}
            </span>
            <span onClick={() => onChange(!checked)}>{label}</span>
        </label>
    );
}

function TerritorySelect({ territories, value, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const containerRef = useRef(null);
    const selected = territories.find((t) => t.territory_id === value);

    // Close on outside click / Escape
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (!containerRef.current?.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const q = query.trim().toLowerCase();
    const filtered = q
        ? territories.filter(
              (t) =>
                  t.territory_id.toLowerCase().includes(q) ||
                  (t.territory_name || "").toLowerCase().includes(q) ||
                  (t.region || "").toLowerCase().includes(q) ||
                  (t.anchor_town || "").toLowerCase().includes(q)
          )
        : territories;

    const selectOption = (t) => {
        onChange(t.territory_id);
        setQuery("");
        setOpen(false);
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`${inputCls} text-left flex items-center justify-between gap-2`}
                data-testid="admin-territory-toggle"
            >
                <span className={selected ? "text-black" : "text-stone-500"}>
                    {selected
                        ? `${selected.territory_id} · ${selected.territory_name}`
                        : "Select a territory\u2026"}
                </span>
                <ChevronDown
                    size={16}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <div className="absolute z-30 mt-2 w-full bg-white border-2 border-black rounded-2xl shadow-[4px_4px_0_0_#0A0A0A] overflow-hidden">
                    <div className="p-2 border-b-2 border-black flex items-center gap-2 bg-[#F1E8D5]">
                        <Search size={14} className="text-stone-600" />
                        <input
                            type="text"
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search territory, region or town\u2026"
                            className="flex-1 bg-transparent outline-none text-sm placeholder:text-stone-500"
                            data-testid="admin-territory-search"
                        />
                    </div>
                    <ul className="max-h-64 overflow-y-auto" data-testid="admin-territory-list">
                        {filtered.length === 0 ? (
                            <li className="px-4 py-3 text-sm text-stone-500">No territories match.</li>
                        ) : (
                            filtered.slice(0, 200).map((t) => (
                                <li key={t.territory_id}>
                                    <button
                                        type="button"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            selectOption(t);
                                        }}
                                        className={`w-full text-left px-4 py-2 hover:bg-[#F1E8D5] text-sm flex items-center justify-between gap-2 ${
                                            t.territory_id === value ? "bg-[#F1E8D5]" : ""
                                        }`}
                                        data-testid={`admin-territory-option-${t.territory_id}`}
                                    >
                                        <span>
                                            <span className="font-mono text-[11px] text-stone-500">{t.territory_id}</span>
                                            <span className="ml-2 font-medium">{t.territory_name}</span>
                                        </span>
                                        <span className="text-[10px] text-stone-500 font-mono uppercase">
                                            {t.region || t.nation || ""}
                                        </span>
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                    {filtered.length > 200 && (
                        <div className="px-4 py-2 text-[10px] font-mono text-stone-500 border-t border-black/10">
                            Showing 200 of {filtered.length} — refine the search to narrow further.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
